if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      /** Bumped on each option change; stale fetch callbacks must not touch variant id. */
      variantFetchGeneration = 0;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.syncVariantFromPicker('connectedCallback');
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      /**
       * Align hidden input[name="id"] with checked option radios (covers defer / early-click gaps).
       */
      syncVariantFromPicker(source = 'syncVariantFromPicker') {
        const variantSelects = this.variantSelectors;
        const matchingVariant = variantSelects?.getMatchingVariant?.();

        if (!matchingVariant?.id) return false;

        variantSelects.reconcileSizeSelectionUI?.();
        this.updateVariantInputs(matchingVariant.id);
        return true;
      }

      resyncAfterPageRestore(source = 'page-restore') {
        this.variantFetchGeneration = (this.variantFetchGeneration ?? 0) + 1;
        this.abortController?.abort();

        VariantSelects.removeHiddenVariantSelectClones();

        const variantSelects = this.variantSelectors;
        variantSelects?.updateAvailableOptions?.();
        variantSelects?.reconcileSizeSelectionUI?.();
        VariantSelects.capturePendingSelectionsFromDom(this);

        const synced = this.syncVariantFromPicker(source);
        const hasCheckedSize = Boolean(
          variantSelects?.querySelector('[data-size-option-fieldset] input[type="radio"]:checked'),
        );

        if (synced && hasCheckedSize) {
          this.setAttribute('data-size-selected', 'true');
          window.sizeSelected = true;
          const productForm = this.productForm;
          if (productForm) {
            productForm._hasCleanedSizeValidation = true;
          }
        } else {
          this.setAttribute('data-size-selected', 'false');
          window.sizeSelected = false;
          this.resetProductFormSizeValidation();
        }

        return synced;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((_newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, force = false } }) {
        if (!force && !this.contains(event?.target)) return;

        this.variantFetchGeneration += 1;

        this.resetProductFormState();
        // this.resetSelectedSwatchState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      resetProductFormSizeValidation() {
        const productForm = this.productForm;
        if (productForm?.resetSizeValidation) {
          productForm.resetSizeValidation();
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const fetchGeneration = this.variantFetchGeneration;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (fetchGeneration !== this.variantFetchGeneration) return;

            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            console.error('ProductInfo: Failed to fetch product info', error);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          this.variantSelectors?.reconcileSizeSelectionUI?.();
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const serverVariant = this.getSelectedVariant(html);

          this.updateOptionValues(html);

          // viewTransition + connectedCallback run synchronously, but defer variant-id
          // resolution so pending selection restore and availability toggles finish first.
          queueMicrotask(() => {
            if (!this.isConnected) return;

            const liveVariant = this.variantSelectors?.getMatchingVariant?.();
            const hasCheckedSize = Boolean(
              this.variantSelectors?.querySelector('[data-size-option-fieldset] input[type="radio"]:checked'),
            );

            // Trust live picker state over section HTML when they disagree (prevents size 5 id + 8.5 UI).
            const resolvedVariant = liveVariant ?? (hasCheckedSize ? null : serverVariant);

            this.pickupAvailability?.update(resolvedVariant);
            this.variantSelectors?.reconcileSizeSelectionUI?.();
            this.updateVariantInputs(resolvedVariant?.id);

            if (!resolvedVariant) {
              this.variantSelectors?.clearInvalidSizeSelection();
              this.setAttribute('data-size-selected', 'false');
              window.sizeSelected = false;
              this.resetProductFormSizeValidation();
              this.setUnavailable();
              return;
            }

            const mediaVariantId = resolvedVariant?.featured_media?.id ?? serverVariant?.featured_media?.id;
            this.updateMedia(html, mediaVariantId);

            const updateSourceFromDestination = (id, shouldHide = (_source) => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            };

            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('low-stock-messaging');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

            // No need to trigger button changes until size selected
            if (window.sizeSelected) {
              if (html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled')) {
                publish(PUB_SUB_EVENTS.submitButtonChange, {
                  disabled: true,
                  text: window.variantStrings.soldOut,
                  froze: true,
                });
              } else {
                this.addToCartComponent.unfreeze();
                this.addToCartComponent.enable();
                this.addToCartComponent.setDefaultTextValue();
              }
            }

            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant: resolvedVariant,
              },
            });
          });
        };
      }

      // resetSelectedSwatchState() {
      //   const labels = document.querySelectorAll('.color-selector');
      //   const inputs = document.querySelectorAll('.color-selector__radio');
      //   labels.forEach((label) => label.classList.remove('is-checked'));
      //   inputs.forEach((input) => {
      //     input.checked = false;
      //     input.removeAttribute('checked');
      //     input.setAttribute('aria-checked', 'false');
      //   });
      // }

      updateVariantInputs(variantId) {
        const forms = this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        );

        forms.forEach((formEl) => {
          const input = formEl.querySelector('input[name="id"]');
          if (!input) return;

          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        publish(PUB_SUB_EVENTS.submitButtonChange, {
          disabled: true,
          text: window.variantStrings.unavailable,
          remember: true,
          froze: true,
        });

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          // eslint-disable-next-line no-useless-assignment
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get addToCartComponent() {
        return this.querySelector('add-to-cart-button');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );

  const syncAllProductInfoVariantsFromPicker = (source) => {
    document
      .querySelectorAll('product-info[data-mvp-product-section], product-info[id^="MainProduct"]')
      .forEach((productInfo) => {
        if (typeof productInfo.syncVariantFromPicker === 'function') {
          productInfo.syncVariantFromPicker(source);
        }
      });
  };

  document.addEventListener(
    'product-info:loaded',
    (event) => {
      const productInfo = event.target;
      if (typeof productInfo?.syncVariantFromPicker === 'function') {
        productInfo.syncVariantFromPicker('product-info:loaded');
      }
    },
    true,
  );

  const runPdpVariantBootstrap = () => {
    queueMicrotask(() => syncAllProductInfoVariantsFromPicker('bootstrap'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runPdpVariantBootstrap, { once: true });
  } else {
    runPdpVariantBootstrap();
  }

  window.addEventListener('pageshow', (event) => {
    const navEntry = performance.getEntriesByType?.('navigation')?.[0];
    const isBackNavigation = event.persisted || navEntry?.type === 'back_forward';

    if (!isBackNavigation) return;

    queueMicrotask(() => {
      VariantSelects.resyncAllAfterPageRestore(event.persisted ? 'pageshow-bfcache' : 'pageshow-back');
    });
  });
}

class PaymentMessaging extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    const popupId = this.getAttribute('data-popup-id');
    const infoClass = this.getAttribute('data-info-class');
    const button = this.querySelector(`.${infoClass}`);
    const popup = document.getElementById(popupId);

    if (!button || !popup) return;

    button.addEventListener('click', () => {
      if (popup.isOpen) {
        popup.close();
      } else {
        popup.open(button);
      }
    });
  }
}

customElements.define('payment-messaging', PaymentMessaging);
